import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Kanban, ChevronRight, CheckCircle, Clock, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Layout from '@/components/Layout';
import { api, toast } from '../App';

const ProjectPlanningList = ({ user }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await api.get('/projects');
        setProjects(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
        setLoading(false);
      }
    };
    loadProjects();
  }, []);

  // Filter projects based on search
  const filteredProjects = projects.filter(project => {
    const search = searchTerm.toLowerCase();
    return (
      project.project_number?.toLowerCase().includes(search) ||
      project.name?.toLowerCase().includes(search) ||
      project.client_name?.toLowerCase().includes(search)
    );
  });

  // Get planning status badge with MD3 styling
  const getPlanningStatusBadge = (project) => {
    if (project.planning_locked) {
      return <Badge className="bg-md3-tertiary-container text-md3-on-tertiary-container">Locked</Badge>;
    }
    if (project.allocated_hours > 0) {
      return <Badge className="bg-md3-primary-container text-md3-on-primary-container">In Progress</Badge>;
    }
    return <Badge variant="outline" className="border-md3-outline text-md3-on-surface-variant">Not Started</Badge>;
  };

  // RBAC Permission Check
  const permissions = user?.permissions?.projectPlanning || {};
  const canView = permissions?.view ?? true;

  // Calculate stats
  const totalProjects = projects.length;
  const lockedCount = projects.filter(p => p.planning_locked).length;
  const inProgressCount = projects.filter(p => p.allocated_hours > 0 && !p.planning_locked).length;
  const notStartedCount = projects.filter(p => !p.allocated_hours || p.allocated_hours === 0).length;

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header - MD3 Style */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface">Project Planning</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant mt-1">
              Manage resource allocation and planning for all projects
            </p>
          </div>
        </div>

        {/* Summary Cards - MD3 Style */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-md3-surface-container-low border-0 shadow-md3-1">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-md3-label-medium text-md3-on-surface-variant">Total Projects</p>
                  <p className="text-md3-display-small text-md3-on-surface">{totalProjects}</p>
                </div>
                <div className="h-12 w-12 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                  <Kanban className="h-6 w-6 text-md3-on-primary-container" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-md3-surface-container-low border-0 shadow-md3-1">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-md3-label-medium text-md3-on-surface-variant">Planning Locked</p>
                  <p className="text-md3-display-small text-md3-on-surface">{lockedCount}</p>
                </div>
                <div className="h-12 w-12 rounded-md3-full bg-md3-tertiary-container flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-md3-on-tertiary-container" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-md3-surface-container-low border-0 shadow-md3-1">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-md3-label-medium text-md3-on-surface-variant">In Progress</p>
                  <p className="text-md3-display-small text-md3-on-surface">{inProgressCount}</p>
                </div>
                <div className="h-12 w-12 rounded-md3-full bg-md3-secondary-container flex items-center justify-center">
                  <Clock className="h-6 w-6 text-md3-on-secondary-container" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-md3-surface-container-low border-0 shadow-md3-1">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-md3-label-medium text-md3-on-surface-variant">Not Started</p>
                  <p className="text-md3-display-small text-md3-on-surface">{notStartedCount}</p>
                </div>
                <div className="h-12 w-12 rounded-md3-full bg-md3-error-container flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-md3-on-error-container" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search - MD3 Style */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-md3-on-surface-variant" />
            <Input
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-md3-surface-container border-md3-outline rounded-md3-full"
            />
          </div>
        </div>

        {/* Projects Table - MD3 Style */}
        <div className="bg-md3-surface-container rounded-md3-lg shadow-md3-1">
          <div className="p-4 border-b border-md3-outline-variant">
            <h2 className="text-md3-title-large text-md3-on-surface">All Projects</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-md3-on-surface-variant">Loading projects...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-md3-outline-variant">
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant">Project #</TableHead>
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant">Name</TableHead>
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant">Client</TableHead>
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant">Allocated Hours</TableHead>
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant">Planning Status</TableHead>
                  <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map(project => (
                  <TableRow key={project.id} className="border-md3-outline-variant hover:bg-md3-surface-container-high">
                    <TableCell className="text-md3-body-medium text-md3-on-surface font-medium">
                      {project.project_number || '-'}
                    </TableCell>
                    <TableCell className="text-md3-body-medium text-md3-on-surface">{project.name}</TableCell>
                    <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                      {project.client_name || '-'}
                    </TableCell>
                    <TableCell className="text-md3-body-medium text-md3-on-surface">
                      {project.allocated_hours || 0} hrs
                    </TableCell>
                    <TableCell>{getPlanningStatusBadge(project)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/projects/${project.id}/planning`)}
                        disabled={!canView}
                        className="text-md3-primary hover:bg-md3-primary-container hover:text-md3-on-primary-container rounded-md3-full"
                      >
                        <ChevronRight className="h-4 w-4 mr-1" />
                        View Planning
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-md3-on-surface-variant py-8">
                      No projects found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProjectPlanningList;
